import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SubscriptionState {
  plan: 'free' | 'premium';
  monthlyUsage: number;
  plantCount: number;
  canIdentify: boolean;
  canDiagnose: boolean;
  canAddPlant: boolean;
  loading: boolean;
  subscriptionEnd: string | null;
}

const FREE_LIMITS = {
  monthlyAiUsage: 3,
  maxPlants: 3,
};

export function useSubscription() {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    plan: 'free',
    monthlyUsage: 0,
    plantCount: 0,
    canIdentify: true,
    canDiagnose: true,
    canAddPlant: true,
    loading: true,
    subscriptionEnd: null,
  });

  const checkSubscription = useCallback(async () => {
    if (!user || !session) {
      setState(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      // Check subscription status from Stripe
      const { data: subData, error: subError } = await supabase.functions.invoke('check-subscription');
      
      if (subError) {
        console.error('Error checking subscription:', subError);
      }

      // If not authenticated (expired session), return early with free defaults
      if (subData?.authenticated === false) {
        console.log('Session expired or not authenticated, using free defaults');
        setState(prev => ({ ...prev, loading: false }));
        return;
      }

      const plan = subData?.plan_type === 'premium' ? 'premium' : 'free';
      const subscriptionEnd = subData?.subscription_end || null;

      // Get monthly usage count
      const { data: usageData, error: usageError } = await supabase.rpc('get_monthly_ai_usage', {
        p_user_id: user.id
      });

      if (usageError) {
        console.error('Error getting usage:', usageError);
      }

      const monthlyUsage = usageData || 0;

      // Get plant count
      const { count: plantCount, error: plantError } = await supabase
        .from('plant_identifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (plantError) {
        console.error('Error getting plant count:', plantError);
      }

      const currentPlantCount = plantCount || 0;

      // Calculate permissions
      const isPremium = plan === 'premium';
      const canIdentify = isPremium || monthlyUsage < FREE_LIMITS.monthlyAiUsage;
      const canDiagnose = isPremium || monthlyUsage < FREE_LIMITS.monthlyAiUsage;
      const canAddPlant = isPremium || currentPlantCount < FREE_LIMITS.maxPlants;

      setState({
        plan,
        monthlyUsage,
        plantCount: currentPlantCount,
        canIdentify,
        canDiagnose,
        canAddPlant,
        loading: false,
        subscriptionEnd,
      });
    } catch (error) {
      console.error('Error in useSubscription:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [user, session]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const logUsage = async (usageType: 'identification' | 'diagnostic') => {
    if (!user) return;

    const { error } = await supabase
      .from('ai_usage_logs')
      .insert({
        user_id: user.id,
        usage_type: usageType,
      });

    if (error) {
      console.error('Error logging usage:', error);
    }

    // Refresh state
    await checkSubscription();
  };

  const refreshSubscription = checkSubscription;

  return {
    ...state,
    logUsage,
    refreshSubscription,
    limits: FREE_LIMITS,
  };
}
